import Button from "components/ui/Button";
import { ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";
import { trackEvent } from "services/productAnalytics";
import Dialog from "./index";
import ApiErrorBanner from "components/ApiErrorBanner";
import styles from "./ConfirmDialog.module.scss";

type BaseConfirmDialogProps = {
  title: string;
  // Short confirmation text; use children for richer bodies (either works alone).
  message?: ReactNode;
  confirmLabel: string;
  // Cancel label; defaults to the shared Common.Cancel translation.
  cancelLabel?: string;
  // Renders the confirm action in the destructive style.
  danger?: boolean;
  // Called with true after a successful confirm, with no argument on cancel.
  onClose: (confirmed?: boolean) => void;
  // Opts into product analytics under this id; see Dialog's analyticsId.
  analyticsId?: string;
  children?: ReactNode;
};

type ConfirmDialogProps =
  | (BaseConfirmDialogProps & {
      acknowledgeOnly?: false;
      // The action to run on confirm; a rejection keeps the dialog open with the error.
      onConfirm: () => Promise<unknown>;
    })
  | (BaseConfirmDialogProps & {
      // Single-acknowledgement mode: no Cancel, the confirm label is the OK label.
      acknowledgeOnly: true;
      // Optional in acknowledge mode, and may be synchronous.
      onConfirm?: () => unknown;
    });

export default function ConfirmDialog(props: ConfirmDialogProps) {
  const { t } = useTranslation();
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<unknown>(null);

  // An acknowledgement offers no choice, so there is nothing to resolve: those
  // dialogs are covered by the generic dialog_opened/dialog_closed pair alone.
  const confirmId = props.acknowledgeOnly ? undefined : props.analyticsId;

  const trackOutcome = (outcome: "confirmed" | "cancelled") => {
    if (confirmId) {
      trackEvent("confirm_dialog_resolved", {
        dialog: confirmId,
        outcome,
        danger: Boolean(props.danger),
      });
    }
  };

  const runConfirm = async () => {
    setPending(true);
    setError(null);
    try {
      await props.onConfirm?.();
      trackOutcome("confirmed");
      props.onClose(true);
    } catch (err) {
      if (confirmId) {
        trackEvent("confirm_dialog_failed", { dialog: confirmId });
      }
      // A rejection with no value still has to say something, and the banner
      // renders nothing for any falsy error — an empty string included.
      setError(err || t("Common.errors.somethingWrong"));
    } finally {
      // Reset on the way out too: a caller that answers a confirm with a second
      // ConfirmDialog reuses this element, and would inherit a stuck action.
      setPending(false);
    }
  };

  // While the action runs the footer's Cancel is disabled, so the ✕, Escape and
  // the overlay must not offer a way around it — a dismissal there would leave
  // the in-flight confirm to report into a closed dialog.
  const dismiss = () => {
    if (!pending) {
      trackOutcome("cancelled");
      props.onClose();
    }
  };

  return (
    <Dialog
      open
      size="xs"
      title={props.title}
      analyticsId={props.analyticsId}
      onClose={dismiss}
      secondaryAction={
        !props.acknowledgeOnly && (
          <Button size="small" variant="secondary" onClick={dismiss} disabled={pending}>
            {props.cancelLabel ?? t("Common.actions.cancel")}
          </Button>
        )
      }
      mainAction={
        <Button
          size="small"
          variant="primary"
          tone={props.danger ? "danger" : "neutral"}
          onClick={() => void runConfirm()}
          loading={pending}
        >
          {props.confirmLabel}
        </Button>
      }
    >
      <ApiErrorBanner error={error} />
      {props.message !== undefined && props.message !== null && (
        <p className={styles.message}>{props.message}</p>
      )}
      {props.children}
    </Dialog>
  );
}
